import React, { createRef } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { assignTasksTo, unassignTasks, setChannelWorkflowGridState, setConfirmationModalState } from 'store/actions';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { push } from 'connected-react-router';
import {
  Chip,
  Popover,
  MenuList,
  MenuItem,
  Divider
} from '@material-ui/core';
import {
  Face as FaceIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@material-ui/icons';
import {
  selectedAuctionTxnOppsSelector
} from 'store/selectors';
import { Constants } from 'utilities/constants';

const styles = theme => ({
  userChip: {
    marginLeft: '16px'
  },
});

const chipRef = createRef();

const ChipDropdown = ({ classes, user, push, selectedAuctionTxnOpps, selectedTask, setChannelWorkflowGridState, assignTasksTo, unassignTasks,
  setConfirmationModalState }) => {
  const [chipOpen, setChipOpen] = React.useState(false);

  const auctionOwnerUsername = selectedAuctionTxnOpps[0]?.auctionOwnerUsername;
  const auctionOwnerUserId = selectedAuctionTxnOpps[0]?.auctionOwnerUserId;
  const isUserTask = user.id === auctionOwnerUserId;

  function handleChipClick(e) {
    setChipOpen(true);
  }

  function handleClose() {
    setChipOpen(false);
  }

  function handleMenuItemClick(item) {
    handleChipSelect(item);
    handleClose();
  }

  const handleChipSelect = e => {
    switch (e.value) {
      case 'copy':
        return setTimeout(() => copyToClipboard(selectedTask.id), 100);
      case 'channelWorkflow':
        return viewAuctionOnChannelWorkflow(selectedTask.id);
      case 'assign':
        return handleAssignAuction();
      case 'unassign':
        return handleUnassignAuction();
      default:
        return;  
    }
  }

  function copyToClipboard(string) {
    const focusEl = document.activeElement
    const dummy = document.createElement('textarea');
    focusEl.appendChild(dummy); // When in viewValuationDialog, MUI enforces focus on the dialog, preventing the dummy from selecting if appended to body. Append to element in dialog instead (potentially visible if it runs slow, can either look into hiding or just disableEnforceFocus for dialog)
    dummy.value = string;
    dummy.select();
    document.execCommand('copy');
    focusEl.removeChild(dummy);
  }

  const viewAuctionOnChannelWorkflow = id => {

    setChannelWorkflowGridState({ filterValue: [['portfolioId', '=', id]] }, true);
    push(Constants.Routes.CHANNEL_WORKFLOW);
  }
  const handleAssignAuction = () => {
    if (auctionOwnerUserId != null && !isUserTask) {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => handleAssignAuctionConfirmation(),
        handleCancellation: closeConfirmationModal,
        titleText: 'Confirm',
        contentText: `This auction is currently assigned to ${auctionOwnerUsername}. Are you sure you want to assign it to yourself?`
      });
    } else {
      assignTasksTo('auction', [selectedTask.id], user.id, user.id);
    }
  }

  function handleAssignAuctionConfirmation() {
    closeConfirmationModal();   // close confirm modal first
    assignTasksTo('auction', [selectedTask.id], user.id, user.id);
  }

  function handleUnassignAuction() {
    if (auctionOwnerUserId != null && !isUserTask) {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => handleUnassignAuctionConfirmation(),
        handleCancellation: closeConfirmationModal,
        titleText: 'Confirm',
        contentText: `This auction is currently assigned to ${auctionOwnerUsername}. Are you sure you want to unassign it?`
      });
    } else {
      unassignTasks('auction', [selectedTask.id], user.id);
    }
  }
  function handleUnassignAuctionConfirmation() {
    closeConfirmationModal();   // close confirm modal first
    unassignTasks('auction', [selectedTask.id], user.id);
  }

  function closeConfirmationModal() {
    setConfirmationModalState({ isModalOpen: false });
  }

  let assignItems = [];
  if (!isUserTask) {
    assignItems.push({ text: 'Assign Auction to Me', value: 'assign' })
  }
  if (auctionOwnerUserId != null) {
    assignItems.push({ text: 'Unassign Auction', value: 'unassign' });
  }

  return (
    <>
      <Chip
        ref={chipRef}
        clickable
        icon={auctionOwnerUsername ? <FaceIcon /> : null}
        label={auctionOwnerUsername || 'Unassigned'}
        className={classes.userChip}
        color={auctionOwnerUsername ? 'primary' : 'default'}
        variant={auctionOwnerUsername ? 'default' : 'outlined'}
        onClick={handleChipClick}
        onDelete={handleChipClick}
        deleteIcon={<ArrowDropDownIcon style={{color: auctionOwnerUserId == null ? '#000' : '#fff'}} />}
      />
      <Popover
        open={chipOpen}
        anchorEl={chipRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuList>
          {[{
            text: 'Copy ID to Clipboard',
            value: 'copy'
          }, 
          ...assignItems,
          {
            text: 'View Auction on Workflow',
            value: 'channelWorkflow'
          }].map((item) => {
            if (!item) return null;
            return (
              item.separator
                ? <Divider key={'divider'} />
                : <MenuItem
                  key={item.text}
                  onClick={() => handleMenuItemClick(item)}
                  disabled={item.disabled}
                >
                  {item.text}
                </MenuItem>
            )
          })}
        </MenuList>
      </Popover>
    </>
  );
};

const mapDispatch = (dispatch) => ({
  assignTasksTo: (taskType, taskIds, assignToUserId, initiatedUserId) =>
    dispatch(assignTasksTo(taskType, taskIds, assignToUserId, initiatedUserId)),
  unassignTasks: (taskType, taskIds, initiatedUserId) =>
    dispatch(unassignTasks(taskType, taskIds, initiatedUserId)),
  setChannelWorkflowGridState: (state, isUpdate) => dispatch(setChannelWorkflowGridState(state, isUpdate)),
  setConfirmationModalState: confirmationModalState => dispatch(setConfirmationModalState(confirmationModalState)),
  push: route => dispatch(push(route)),
});

const mapState = (state) => ({
  user: state.appState.user,
  selectedAuctionTxnOpps: selectedAuctionTxnOppsSelector(state)
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapState, mapDispatch)
)(ChipDropdown);


